import axios from "axios";
import { filter, set } from "lodash";
import { nanoid } from "nanoid";

import { deprecatedPropType } from "@mui/material";

import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { AirportMapItem } from "@/constants/airport";
import {
  API_KEY,
  FLIGHT_BATCH_SIZE,
  IsProd,
  MAX_FLIGHT_BATCH_SIZE,
  isLocal,
} from "@/constants/config";
import { MAX_PRO_DATE_RANGE } from "@/constants/config";
import {
  RATE_LIMIT_BETWEEN_FIVE_REQUESTS,
  RATE_LIMIT_BETWEEN_REQUESTS,
  RATE_LIMIT_BETWEEN_TWO_REQUESTS,
} from "@/constants/config";
import { MAX_MEGA_FLIGHT_RANGE, MEMBERSHIP_CONFIG } from "@/constants/membership";
import { PROGRAM_NAME_MAP } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import request from "@/utils/request";

import { flightTaskCrawlerV2 } from "./flight";
import { recordFirstClass, recordMega } from "./record";
import { recordFlights } from "./record";
import { getGroupDatesV2 } from "./routes";

const PATH_PREFIX = IsProd ? "/v1/" : "/vb/";

function filterFilightData(item: any) {
  if (
    (item["cabin_type"] === "Economy" || item["cabin_type"] === "Premium Economy") &&
    item["percent_premium"] > 0
  ) {
    return false;
  }
  return true;
}

function generateUUID() {
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  return uuid;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const field_mapping: any = {
  pm: "payment",
  or: "origin",
  d_t: "departure_time",
  a_t: "arrival_time",
  de: "destination",
  c_t: "cabin_type",
  o_c_t: "final_cabin_type",
  ac: "aircraft",
  a_c: "airline_code",
  a_n: "airline_name",
  f_n: "flight_number",
  l_t: "layover_time",
  r_t: "return_trip",
  t_m: "travel_minutes",
  p_c: "program_code",
  p_n: "program_name",
  a_p: "award_points",
  sc: "surcharge",
  m_a: "mixed_airline",
  p_p: "percent_premium",
  t_m_t: "travel_minutes_total",
  t_t_d: "travel_time_display",
  d_c: "day_count",
  ps: "products",
  c: "currency",
  t: "tax",
  m: "miles",
  ca: "cabin",
  t_o: "transfer_options",
  cf: "cash_fare",
  c_f_r: "cash_fare_range",
};

function mapField(item: any, addId = false) {
  Object.keys(item).map((key) => {
    // Check if key is in field_mapping
    if (field_mapping.hasOwnProperty(key)) {
      item[field_mapping[key]] = item[key];
      // Delete old key
      delete item[key];
    }

    if (addId) {
      item.hash = nanoid();
    }
  });
}

function mapProduct(item: any) {
  item["airline_name"] = AIRLINE_NAME_MAP[item["airline_code"]];
}

const transform_item = async (item: any) => {
  // Mapping each field to a more readable name based on field_mapping
  await mapField(item, true);
  await mapField(item.fare);
  await item.fare.products.map(async (product: any) => {
    await mapField(product, true);
    mapProduct(product);
  });
  item["program_name"] = PROGRAM_NAME_MAP.get(item["program_code"]);
  if (item && item.hasOwnProperty("transfer_options")) {
    item["transfer_options"]?.map((option: any) => {
      item["transfer_url"] = PROGRAM_NAME_MAP.get(option["program_code"]);
    });
  }

  // Processing floating fields
  const floating_fields = ["surcharge", "award_points", "cash_fare"];
  // Get to the second decimal place if there is decimal, otherwise, keep it as it is
  floating_fields.map((field) => {
    if (item[field] && item[field].toString().includes(".")) {
      item[field] = parseFloat(item[field]).toFixed(2);
    }
  });
};

const getDate = (dateString: string) => {
  const dateParts = dateString.split("-");
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JavaScript, so subtract 1
  const day = parseInt(dateParts[2], 10);

  const dateObject = new Date(year, month, day);
  return dateObject;
};

export const markGroupDates = (
  startDateStr: string,
  endDateStr: string,
  groupSize: number,
  mark: string
) => {
  const startDate = getDate(startDateStr);
  const endDate = getDate(endDateStr);

  const datesBetween = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");

    datesBetween.push([`${year}-${month}-${day}`, mark]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return datesBetween;
};

export const getGroupDates = (
  startDateStr: string,
  endDateStr: string,
  groupSize: number,
  mark: string
) => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  const datesBetween = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    datesBetween.push([currentDate.toISOString().split("T")[0], mark]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  datesBetween.push(currentDate.toISOString().split("T")[0]);

  const groups = [];
  let group = [];

  for (let i = 0; i < datesBetween.length; i++) {
    group.push(datesBetween[i]);
    if ((i + 1) % groupSize === 0 || i === datesBetween.length - 1) {
      groups.push(group);
      group = [];
    }
  }

  return groups;
};

export const getGroupDatesEvenly = (
  startDateStr: string,
  endDateStr: string,
  groupSize: number
) => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  const datesBetween = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    datesBetween.push(currentDate.toISOString().split("T")[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return splitArrayEvenly(datesBetween, 2);
};

const getDateDiffCount = (date1: string, date2: string) => {
  // Get the difference between two dates
  const date1Obj = new Date(date1);
  const date2Obj = new Date(date2);
  const diffTime = Math.abs(date2Obj.getTime() - date1Obj.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

const dateBeyongToday = (date: string) => {
  if (date === "") {
    return 0;
  }
  const dateObj = new Date(date);
  const today = new Date();
  return (dateObj.getTime() - today.getTime()) / (1000 * 60 * 60 * 24) + 1;
};

function splitArray(array: any[], size: number) {
  const result = [];

  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }

  return result;
}

function splitArrayEvenly(array: any[], size: number) {
  const result = [];
  const group_size = Math.ceil(array.length / size);
  for (let i = 0; i < array.length; i += group_size) {
    result.push(array.slice(i, i + group_size));
  }

  return result;
}

export const getFlightListV2 = async (
  data: {
    search_type: string;
    pax: number;
    children?: number;
    api_key?: string | undefined;
    cabins: string[];
    departure_airports: string[];
    arrival_airports: string[];
    date_range?: {
      from: string;
      to: string;
    };
    return_date_range?: {
      from: string;
      to: string;
    };
    type?: string | undefined;
    task_id: string;
    task_ct: number;
    batch: number | undefined;
    batch_size: number | undefined;
    round_count: number | undefined;
    pro_user: boolean | undefined;
    routes: object[] | undefined;
    programs: string[];
    raw_path?: string;
  },
  appendSearchData: any,
  id_token: string | undefined,
  condition: SearchConditionType,
  showUpsell: boolean,
  openDialog: any,
  openTooFrequentDialog: any,
  setLoading: any,
  cancelToken?: { token: any; cancel: () => void }
) => {
  // Rate limit for real-time search function.
  // Record search timeepoch time in the localstorage
  const now = new Date();
  const time = now.getTime();
  const search_time = localStorage.getItem("last_searches_time");
  let last_searches_time = [];
  if (search_time) {
    last_searches_time = JSON.parse(search_time);
    if (
      time - last_searches_time[last_searches_time.length - 1] < RATE_LIMIT_BETWEEN_REQUESTS ||
      (last_searches_time.length >= 5 &&
        time - last_searches_time[last_searches_time.length - 5] <
        RATE_LIMIT_BETWEEN_FIVE_REQUESTS) ||
      (last_searches_time.length >= 2 &&
        time - last_searches_time[last_searches_time.length - 2] < RATE_LIMIT_BETWEEN_TWO_REQUESTS)
    ) {
      openTooFrequentDialog();
      await setLoading(false);
      return;
    }
  }
  last_searches_time.push(time);
  last_searches_time = last_searches_time.slice(-10);
  localStorage.setItem("last_searches_time", JSON.stringify(last_searches_time));

  const show_upsell = showUpsell;
  const task_id = await generateUUID();
  data.task_id = task_id;
  delete data.type;
  // Get total entry count
  const departure_airports = data.departure_airports || [];
  const arrival_airports = data.arrival_airports || [];
  let entries = departure_airports.length * arrival_airports.length;
  let date_count = 0;
  if (data.return_date_range) {
    date_count =
      date_count + getDateDiffCount(data.return_date_range.from, data.return_date_range.to);
  }
  if (data.date_range) {
    date_count = date_count + getDateDiffCount(data.date_range.from, data.date_range.to);
  }
  entries = entries * date_count;

  if (show_upsell && !condition.rangeV2 && entries > MEMBERSHIP_CONFIG.flight_range[0]) {
    openDialog();
    return;
  }

  if (condition.rangeV2) {
    if (entries > MEMBERSHIP_CONFIG.flight_range[1]) {
      // console.log("SHOW1:", entries)
      openDialog();
      return;
    }

    if (
      show_upsell &&
      (dateBeyongToday(data.date_range?.to || "") > MAX_PRO_DATE_RANGE ||
        dateBeyongToday(data.return_date_range?.to || "") > MAX_PRO_DATE_RANGE)
    ) {
      // console.log("Wrong 3!");
      openDialog();
      return;
    }
  }

  // console.log("PASSSS.", show_upsell);

  // return;

  // Split the departure airports by the size of the group
  let departure_groups = [];
  if (departure_airports.length > 1 && entries > 28) {
    departure_groups = splitArrayEvenly(departure_airports, 8);
  } else if (departure_airports.length > 1 && entries > 24) {
    departure_groups = splitArrayEvenly(departure_airports, 7);
  } else if (departure_airports.length > 1 && entries > 20) {
    departure_groups = splitArrayEvenly(departure_airports, 6);
  } else if (departure_airports.length > 1 && entries > 16) {
    departure_groups = splitArrayEvenly(departure_airports, 5);
  } else if (departure_airports.length > 1 && entries > 12) {
    departure_groups = splitArrayEvenly(departure_airports, 4);
  } else if (departure_airports.length > 1 && entries > 8) {
    departure_groups = splitArrayEvenly(departure_airports, 3);
  } else if (departure_airports.length > 1 && entries > 4) {
    departure_groups = splitArrayEvenly(departure_airports, 2);
  } else {
    departure_groups = [departure_airports];
  }

  let arrival_groups = [];
  if (departure_groups.length > 1) {
    arrival_groups = [arrival_airports];
  } else {
    if (arrival_airports.length > 1 && entries > 28) {
      arrival_groups = splitArrayEvenly(arrival_airports, 8);
    } else if (arrival_airports.length > 1 && entries > 24) {
      arrival_groups = splitArrayEvenly(arrival_airports, 7);
    } else if (arrival_airports.length > 1 && entries > 20) {
      arrival_groups = splitArrayEvenly(arrival_airports, 6);
    } else if (arrival_airports.length > 1 && entries > 16) {
      arrival_groups = splitArrayEvenly(arrival_airports, 5);
    } else if (arrival_airports.length > 1 && entries > 12) {
      arrival_groups = splitArrayEvenly(arrival_airports, 4);
    } else if (arrival_airports.length > 1 && entries > 8) {
      arrival_groups = splitArrayEvenly(arrival_airports, 3);
    } else if (arrival_airports.length > 1 && entries > 4) {
      arrival_groups = splitArrayEvenly(arrival_airports, 2);
    } else {
      arrival_groups = [arrival_airports];
    }
  }

  // Get the date groups
  let outbound_dates: Array<string[]> = [];
  if (data.date_range) {
    outbound_dates = markGroupDates(data.date_range.from, data.date_range.to, 1, "outbound");
  }

  let return_dates_lst: Array<string[]> = [];
  if (data.return_date_range) {
    return_dates_lst = markGroupDates(
      data.return_date_range.from,
      data.return_date_range.to,
      1,
      "return"
    );
  }
  // Concat the two arrays
  const concatenatedArray = outbound_dates.concat(return_dates_lst);
  let date_groups: Array<string[][]> = [];
  if (departure_groups.length > 1 || arrival_groups.length > 1) {
    date_groups = [concatenatedArray];
  } else {
    if (concatenatedArray.length > 1 && entries > 25) {
      date_groups = splitArrayEvenly(concatenatedArray, 8);
    } else if (concatenatedArray.length > 1 && entries > 24) {
      date_groups = splitArrayEvenly(concatenatedArray, 7);
    } else if (concatenatedArray.length > 1 && entries > 20) {
      date_groups = splitArrayEvenly(concatenatedArray, 6);
    } else if (concatenatedArray.length > 1 && entries > 16) {
      date_groups = splitArrayEvenly(concatenatedArray, 5);
    } else if (concatenatedArray.length > 1 && entries > 4 && entries > 12) {
      date_groups = splitArrayEvenly(concatenatedArray, 4);
    } else if (concatenatedArray.length > 1 && entries > 4 && entries > 8) {
      date_groups = splitArrayEvenly(concatenatedArray, 3);
    } else if (concatenatedArray.length > 1 && entries > 4) {
      date_groups = splitArrayEvenly(concatenatedArray, 2);
    } else {
      date_groups = [concatenatedArray];
    }
  }

  const FINAL_BATCH_SIZE = id_token ? 11 : 5;
  // return;
  const raw_path = "/search";
  const task_number =
    departure_groups.length * arrival_groups.length * date_groups.length * FINAL_BATCH_SIZE;
  // let timeout_task = -100000;
  data.task_ct = task_number;
  if (IsProd) {
    delete data.api_key;
  }

  // const search_task_api = IsProd
  //   ? "https://www.awardtool-api.com/task_crawler_v2"
  //   : "https://www.awardtool-api.com/task_crawler_v2";
  // const search_task_api = "https://apisv2.awardtoolapi.com/api/task_crawler_v2"
  const search_task_api = "https://apisv2.awardtoolapi.com/api/task_crawler_v2";
  // const search_task_api = "http://0.0.0.0:80/task_crawler_v2"
  let task_count = 0;
  const task_done_local = 0;
  let round_count = 0;
  const tasks = [];
  for (let m = 0; m < departure_groups.length; m++) {
    for (let n = 0; n < arrival_groups.length; n++) {
      for (let i = 0; i < date_groups.length; i++) {
        round_count += 1;

        for (let b = 0; b < FINAL_BATCH_SIZE; b++) {
          // for (let b = 0; b < 1; b++) {
          // Deep copy data object
          const data_copy = (({ raw_path, date_range, return_date_range, ...o }) => o)(data);
          const date_group = date_groups[i];
          // Find the index where the date type changed
          let date_type_index = 0;
          for (let j = 1; j < date_group.length; j++) {
            if (j > 0 && date_group[j][1] !== date_group[j - 1][1]) {
              break;
            }
            date_type_index = j;
          }
          date_type_index += 1;

          // Split the date group into two
          const mini_date_groups_1 = date_group.slice(0, date_type_index);
          const mini_date_groups_2 = date_group.slice(date_type_index, date_group.length);

          // Assign the date range based on the first group
          if (mini_date_groups_1.length) {
            if (mini_date_groups_1[0][1] === "outbound") {
              data_copy.date_range = {
                from: mini_date_groups_1[0][0],
                to: mini_date_groups_1[mini_date_groups_1.length - 1][0],
              };
            } else {
              data_copy.return_date_range = {
                from: mini_date_groups_1[0][0],
                to: mini_date_groups_1[mini_date_groups_1.length - 1][0],
              };
            }
          }

          // Assign the date range based on the second group
          if (mini_date_groups_2.length) {
            if (mini_date_groups_2[0][1] === "outbound") {
              data_copy.date_range = {
                from: mini_date_groups_2[0][0],
                to: mini_date_groups_2[mini_date_groups_2.length - 1][0],
              };
            } else {
              data_copy.return_date_range = {
                from: mini_date_groups_2[0][0],
                to: mini_date_groups_2[mini_date_groups_2.length - 1][0],
              };
            }
          }

          task_count += 1;
          data_copy.arrival_airports = arrival_groups[n];
          data_copy.departure_airports = departure_groups[m];
          data_copy.batch = b;
          data_copy.batch_size = FINAL_BATCH_SIZE;
          data_copy.round_count = round_count;
          data_copy.pro_user = !show_upsell;

          // // console.log(data_copy);
          // // Trigger the tasks
          // if (false) {
          //   request
          //     .post(search_api + "-" + task_count, data_copy)
          //     .then((response) => {
          //       // if (response.status !== 200) {
          //       //   timeout_task += 1;
          //       // } else {
          //       //   task_done_local += 1;
          //       // }
          //     })
          //     .catch((e) => {
          //       // timeout_task += 1;
          //       // console.error("Failed: ", e, timeout_task);
          //     });
          // } else {

          // Trigger/Collect the tasks
          tasks.push(data_copy);
          // request.post("http://0.0.0.0:80/task_crawler_v2" + "-" + task_count, data_copy);
          // }

          // }

          // fetch(search_api + "-" + task_count, {
          //   method: "POST",
          //   headers: {
          //     "Content-Type": "application/json",
          //     Authentication: id_token,
          //   },
          //   body: JSON.stringify(data_copy),
          // })
          //   .then((response) => {
          //     if (response.status !== 200) {
          //       timeout_task += 1;
          //     } else {
          //       task_done_local += 1;
          //     }
          //   })
          //   .catch((e) => {
          //     timeout_task += 1;
          //     console.error("Failed: ", e, timeout_task);
          //   });
        }
      }
    }
  }
  // Copy data and remove task_id key
  const data_copy_2 = { ...data } as any;
  if ("task_id" in data_copy_2) {
    delete data_copy_2.task_id;
  }
  // Send the aggregated tasks to the server
  const results = await flightTaskCrawlerV2({
    tasks: tasks,
    query: JSON.stringify(data_copy_2),
    raw_path: raw_path,
  });

  if (results.message.includes("too frequently")) {
    openTooFrequentDialog();
    return;
  }


  // record flight search
  const flight_response = await recordFlights({
    query: JSON.stringify(data_copy_2),
    type: "flight",
  });
  let record_id = flight_response.record_id;

  // Query the data with trigger type
  data.type = "trigger";
  data.task_ct = task_number;
  await sleep(2000);
  let search_data: any = [];
  let finished = false;
  // const search_result_api = false
  //   ? "http://45.55.128.38:2000/search_result"
  //   : "https://apis.awardtool.com/crawlerv2/search_result";
  const search_result_api = "https://apisv2.awardtoolapi.com/search_result_v2";

  // const search_result_api = "https://crawler.awardtool.com/search_result"
  const query_count = 0;
  // Start time
  const start_time = new Date();
  // Collect data from the server - roame.travel
  while (!finished) {
    try {
      // Check if the request has been cancelled
      if (cancelToken?.token?.isCancelled) {
        console.log("Search request cancelled");
        return;
      }

      const response_json: any = await request.post(
        search_result_api,
        {
          task_id: task_id,
          task_ct: task_number,
          api_key: data.api_key,
          query_count: query_count,
          record_id: record_id,
          raw_path: "/search_result",
        },
        { cancelToken: cancelToken?.token }
      );

      if (response_json.finish || query_count > 50) {
        finished = true;
      }
      record_id = response_json.record_id;
      const response_data = response_json.result || [];
      record_id = response_json.record_id;
      await response_data.map(async (item: any) => {
        transform_item(item);
      });
      const filtered_response_data = await response_data.filter(filterFilightData);
      if (filtered_response_data.length > 0) {
        search_data = search_data.concat(filtered_response_data);
        appendSearchData(search_data);
      }

      if (!finished && !response_json.has_next) {
        await sleep(500);
      }
    } catch (e) {
      // Check if this is a cancellation error
      if (axios.isCancel(e)) {
        console.log("Request cancelled:", e.message);
        return;
      }
      console.warn(e);
      await sleep(800);
    }
  }

  // Check if the request has been cancelled before making the last call
  if (cancelToken?.token?.isCancelled) {
    console.log("Search request cancelled before final call");
    return;
  }

  // Last call
  await setLoading(false);
  await sleep(600);

  try {
    const response_json: any = await request.post(
      search_result_api,
      {
        task_id: task_id,
        task_ct: task_number,
        api_key: data.api_key,
        record_id: record_id,
        query_count: 100,
        raw_path: "/search_result",
      },
      { cancelToken: cancelToken?.token }
    );

    // const { data } = await resposne.json();
    // console.log("TASK STATS: ", timeout_task, task_done_local, task_count);
    const response_data = response_json.result || [];
    await response_data.map(async (item: any) => {
      // Mapping each field to a more readable name based on field_mapping
      transform_item(item);
    });
    const filtered_response_data = response_data.filter(filterFilightData);
    if (filtered_response_data.length > 0) {
      search_data = search_data.concat(filtered_response_data);
      appendSearchData(search_data);
    }
  } catch (e) {
    // Check if this is a cancellation error
    if (axios.isCancel(e)) {
      console.log("Final request cancelled:", e.message);
      return;
    }
    console.warn(e);
  }
};

export const getFlightMegaListV2 = async (
  data: {
    search_type: string;
    pax: number;
    api_key?: string | undefined;
    cabins: string[];
    departure_airports: string[];
    arrival_airports: string[];
    date_range?: {
      from: string;
      to: string;
    };
    return_date_range?: {
      from: string;
      to: string;
    };
    type: string | undefined;
    task_id: string;
    task_ct: number;
    batch: number | undefined;
    batch_size: number | undefined;
    routes: object[] | undefined;
    programs: string[];
    raw_path: string;
  },
  appendSearchData: any,
  id_token: string | undefined,
  condition: SearchConditionType,
  showUpsell: boolean,
  openDialog: any,
  openTooFrequentDialog: any,
  setLoading: any
) => {
  // Rate limit for real-time search function.
  // Record search timeepoch time in the localstorage
  const now = new Date();
  const time = now.getTime();
  const search_time = localStorage.getItem("last_searches_time");
  let last_searches_time = [];
  if (search_time) {
    last_searches_time = JSON.parse(search_time);
    if (
      time - last_searches_time[last_searches_time.length - 1] < RATE_LIMIT_BETWEEN_REQUESTS ||
      (last_searches_time.length >= 3 &&
        time - last_searches_time[last_searches_time.length - 5] <
        RATE_LIMIT_BETWEEN_FIVE_REQUESTS) ||
      (last_searches_time.length >= 2 &&
        time - last_searches_time[last_searches_time.length - 2] < RATE_LIMIT_BETWEEN_TWO_REQUESTS)
    ) {
      openTooFrequentDialog();
      await setLoading(false);
      return;
    }
  }
  last_searches_time.push(time);
  last_searches_time = last_searches_time.slice(-10);
  localStorage.setItem("last_searches_time", JSON.stringify(last_searches_time));

  const show_upsell = showUpsell;
  const task_id = await generateUUID();
  data.task_id = task_id;
  delete data.type;
  // Get total entry count
  const departure_airports = data.departure_airports || [];
  const arrival_airports = data.arrival_airports || [];

  let entries = departure_airports.length * arrival_airports.length;
  let date_count = 0;
  if (data.return_date_range) {
    date_count =
      date_count + getDateDiffCount(data.return_date_range.from, data.return_date_range.to);
  }
  if (data.date_range) {
    date_count = date_count + getDateDiffCount(data.date_range.from, data.date_range.to);
  }
  entries = entries * date_count;

  if (show_upsell && !condition.rangeV2 && entries > MAX_MEGA_FLIGHT_RANGE) {
    openDialog();
    return;
  }

  if (condition.rangeV2) {
    if (entries > MAX_MEGA_FLIGHT_RANGE) {
      // console.log("SHOW1:", entries)
      openDialog();
      return;
    }

    if (
      show_upsell &&
      (dateBeyongToday(data.date_range?.to || "") > MAX_PRO_DATE_RANGE ||
        dateBeyongToday(data.return_date_range?.to || "") > MAX_PRO_DATE_RANGE)
    ) {
      // console.log("Wrong 3!");
      openDialog();
      return;
    }
  }

  // console.log("PASSSS.", show_upsell);

  // return;

  // Split the departure airports by the size of the group

  let departure_groups = [];
  if (departure_airports.length > 1 && entries > 28) {
    departure_groups = splitArrayEvenly(departure_airports, 8);
  } else if (departure_airports.length > 1 && entries > 24) {
    departure_groups = splitArrayEvenly(departure_airports, 7);
  } else if (departure_airports.length > 1 && entries > 20) {
    departure_groups = splitArrayEvenly(departure_airports, 6);
  } else if (departure_airports.length > 1 && entries > 16) {
    departure_groups = splitArrayEvenly(departure_airports, 5);
  } else if (departure_airports.length > 1 && entries > 12) {
    departure_groups = splitArrayEvenly(departure_airports, 4);
  } else if (departure_airports.length > 1 && entries > 8) {
    departure_groups = splitArrayEvenly(departure_airports, 3);
  } else if (departure_airports.length > 1 && entries > 4) {
    departure_groups = splitArrayEvenly(departure_airports, 2);
  } else {
    departure_groups = [departure_airports];
  }

  let arrival_groups = [];
  if (departure_groups.length > 1) {
    arrival_groups = [arrival_airports];
  } else {
    if (arrival_airports.length > 1 && entries > 28) {
      arrival_groups = splitArrayEvenly(arrival_airports, 8);
    } else if (arrival_airports.length > 1 && entries > 24) {
      arrival_groups = splitArrayEvenly(arrival_airports, 7);
    } else if (arrival_airports.length > 1 && entries > 20) {
      arrival_groups = splitArrayEvenly(arrival_airports, 6);
    } else if (arrival_airports.length > 1 && entries > 16) {
      arrival_groups = splitArrayEvenly(arrival_airports, 5);
    } else if (arrival_airports.length > 1 && entries > 12) {
      arrival_groups = splitArrayEvenly(arrival_airports, 4);
    } else if (arrival_airports.length > 1 && entries > 8) {
      arrival_groups = splitArrayEvenly(arrival_airports, 3);
    } else if (arrival_airports.length > 1 && entries > 4) {
      arrival_groups = splitArrayEvenly(arrival_airports, 2);
    } else {
      arrival_groups = [arrival_airports];
    }
  }

  // Get the date groups
  let outbound_dates: Array<string[]> = [];
  if (data.date_range) {
    outbound_dates = markGroupDates(data.date_range.from, data.date_range.to, 1, "outbound");
  }

  let return_dates_lst: Array<string[]> = [];
  if (data.return_date_range) {
    return_dates_lst = markGroupDates(
      data.return_date_range.from,
      data.return_date_range.to,
      1,
      "return"
    );
  }
  // Concat the two arrays
  const concatenatedArray = outbound_dates.concat(return_dates_lst);
  let date_groups: Array<string[][]> = [];
  if (departure_groups.length > 1 || arrival_groups.length > 1) {
    date_groups = [concatenatedArray];
  } else {
    if (concatenatedArray.length > 1 && entries > 25) {
      date_groups = splitArrayEvenly(concatenatedArray, 8);
    } else if (concatenatedArray.length > 1 && entries > 24) {
      date_groups = splitArrayEvenly(concatenatedArray, 7);
    } else if (concatenatedArray.length > 1 && entries > 20) {
      date_groups = splitArrayEvenly(concatenatedArray, 6);
    } else if (concatenatedArray.length > 1 && entries > 16) {
      date_groups = splitArrayEvenly(concatenatedArray, 5);
    } else if (concatenatedArray.length > 1 && entries > 4 && entries > 12) {
      date_groups = splitArrayEvenly(concatenatedArray, 4);
    } else if (concatenatedArray.length > 1 && entries > 4 && entries > 8) {
      date_groups = splitArrayEvenly(concatenatedArray, 3);
    } else if (concatenatedArray.length > 1 && entries > 4) {
      date_groups = splitArrayEvenly(concatenatedArray, 2);
    } else {
      date_groups = [concatenatedArray];
    }
  }

  // const FINAL_BATCH_SIZE = id_token
  //   ? entries >= 3
  //     ? MAX_FLIGHT_BATCH_SIZE
  //     : FLIGHT_BATCH_SIZE
  //   : 5;
  const FINAL_BATCH_SIZE = 1;
  // return;
  const task_number =
    departure_groups.length * arrival_groups.length * date_groups.length * FINAL_BATCH_SIZE;
  // let timeout_task = -10000;
  data.task_ct = task_number;
  if (IsProd) {
    delete data.api_key;
  }

  // const search_mega_api = IsProd
  //   ? "https://flights.awardtoolapi.com/search_mega"
  //   : "https://flights.awardtoolapi.com/search_mega";
  const search_mega_api = "https://www.awardtool-api.com/task_crawler";
  // const search_mega_api = "https://awardtoolapi-prod-83426901010.us-central1.run.app/task_crawler"
  // const search_mega_api = "http://0.0.0.0:80/api/task_crawler";
  let task_count = 0;
  let round_count = 0;
  const raw_path = "/search_mega";

  for (let m = 0; m < departure_groups.length; m++) {
    for (let n = 0; n < arrival_groups.length; n++) {
      for (let i = 0; i < date_groups.length; i++) {
        round_count += 1;
        for (let b = 0; b < FINAL_BATCH_SIZE; b++) {
          // for (let b = 0; b < 1; b++) {
          // Deep copy data object
          const data_copy = (({ raw_path, date_range, return_date_range, ...o }) => o)(data);
          const date_group = date_groups[i];
          // Find the index where the date type changed
          let date_type_index = 0;
          for (let j = 1; j < date_group.length; j++) {
            if (j > 0 && date_group[j][1] !== date_group[j - 1][1]) {
              break;
            }
            date_type_index = j;
          }
          date_type_index += 1;

          // Split the date group into two
          const mini_date_groups_1 = date_group.slice(0, date_type_index);
          const mini_date_groups_2 = date_group.slice(date_type_index, date_group.length);

          // Assign the date range based on the first group
          if (mini_date_groups_1.length) {
            if (mini_date_groups_1[0][1] === "outbound") {
              data_copy.date_range = {
                from: mini_date_groups_1[0][0],
                to: mini_date_groups_1[mini_date_groups_1.length - 1][0],
              };
            } else {
              data_copy.return_date_range = {
                from: mini_date_groups_1[0][0],
                to: mini_date_groups_1[mini_date_groups_1.length - 1][0],
              };
            }
          }

          // Assign the date range based on the second group
          if (mini_date_groups_2.length) {
            if (mini_date_groups_2[0][1] === "outbound") {
              data_copy.date_range = {
                from: mini_date_groups_2[0][0],
                to: mini_date_groups_2[mini_date_groups_2.length - 1][0],
              };
            } else {
              data_copy.return_date_range = {
                from: mini_date_groups_2[0][0],
                to: mini_date_groups_2[mini_date_groups_2.length - 1][0],
              };
            }
          }

          task_count += 1;
          data_copy.arrival_airports = arrival_groups[n];
          data_copy.departure_airports = departure_groups[m];
          data_copy.batch = b;
          data_copy.batch_size = FINAL_BATCH_SIZE;
          data_copy.round_count = round_count;
          // data_copy.pro_user = !show_upsell;

          // console.log(data_copy);
          // Trigger the tasks
          request
            .post(search_mega_api + "-" + task_count, data_copy)
            .then((response) => { })
            .catch((e) => {
              // timeout_task += 1;
              // console.error("Failed: ", e, timeout_task);
            });

          // fetch(search_mega_api + "-" + task_count, {
          //   method: "POST",
          //   headers: {
          //     "Content-Type": "application/json",
          //     Authentication: id_token,
          //   },
          //   body: JSON.stringify(data_copy),
          // })
          //   .then((response) => {
          //     if (response.status !== 200) {
          //       timeout_task += 1;
          //     } else {
          //       task_done_local += 1;
          //     }
          //   })
          //   .catch((e) => {
          //     timeout_task += 1;
          //     console.error("Failed: ", e, timeout_task);
          //   });
        }
      }
    }
  }
  // return;
  // record flight search
  const record_response = await recordMega({ query: JSON.stringify(data), type: "mega" });
  let record_id = record_response.record_id;

  // Query the data with trigger type
  data.type = "trigger";
  data.task_ct = task_number;
  await sleep(2000);
  let search_data: any = [];
  let finished = false;
  const search_result_api = false
    ? "http://45.55.128.38:2000/search_result"
    : "https://apis.awardtool.com/crawler/search_result";

  let query_count = 0;
  // Collect data from the server - roame.travel
  while (!finished) {
    // const resposne = await fetch(search_result_api, {
    //   method: "POST",
    //   headers: {
    //     "Content-Type": "application/json",
    //     Authentication: id_token,
    //   },
    //   body: JSON.stringify({
    //     task_id: task_id,
    //     task_ct: task_number,
    //     api_key: data.api_key,
    //     query_count: query_count,
    //   }),
    // });
    query_count += 1;
    // if (query_count > 30) {
    //   return;
    // }
    // const response_json = await resposne.json();
    try {
      const response_json: any = await request.post(search_result_api, {
        task_id: task_id,
        task_ct: task_number,
        api_key: data.api_key,
        query_count: query_count,
        record_id: record_id,
      });

      if (
        response_json.finish ||
        // timeout_task + (response_json.task_done ? response_json.task_done : 0) >= response_json.task_ct ||
        query_count > 50
      ) {
        // console.log("FINISHED", response_json.finish);
        finished = true;
      }

      record_id = response_json.record_id;
      const response_data = response_json.result || [];
      record_id = response_json.record_id;

      await response_data.map(async (item: any) => {
        transform_item(item);
      });
      const filtered_response_data = await response_data.filter(filterFilightData);
      if (filtered_response_data.length > 0) {
        search_data = search_data.concat(filtered_response_data);
        appendSearchData(search_data);
      }
      // console.log("RESPONSE DATA: ", response_data.length, search_data.length);
      if (!finished && !response_json.has_next) {
        await sleep(300);
      }
    } catch (e) {
      console.warn(e);
      await sleep(500);
    }
  }

  // Last call
  await setLoading(false);
  await sleep(1200);

  const response_json: any = await request.post(search_result_api, {
    task_id: task_id,
    task_ct: task_number,
    record_id: record_id,
    api_key: data.api_key,
    query_count: 100,
  });
  // const { data } = await resposne.json();
  // console.log("TASK STATS: ", timeout_task, task_done_local, task_count);
  const response_data = response_json.result || [];
  await response_data.map(async (item: any) => {
    // Mapping each field to a more readable name based on field_mapping
    transform_item(item);
  });
  const filtered_response_data = response_data.filter(filterFilightData);
  if (filtered_response_data.length > 0) {
    search_data = search_data.concat(filtered_response_data);
    appendSearchData(search_data);
  }
};

export const getFlightListAlertV2 = async (
  data: {
    alert_id: string;
  },
  appendSearchData: any,
  id_token: string | undefined
) => {
  const alert_cache_api = IsProd ? "/v1/alert_cache_data" : "/vb/alert_cache_data";
  const response_json: any = await request.post(alert_cache_api, {
    alert_id: data.alert_id,
  });

  response_json.data.map(async (item: any) => {
    // Mapping each field to a more readable name based on field_mapping
    transform_item(item);
  });
  response_json.all_match_data.map(async (item: any) => {
    transform_item(item);
  });
  appendSearchData(response_json.all_match_data);
};

export const getRouteCheck = async (
  data: {
    fromLocation: AirportMapItem[];
    toLocation: AirportMapItem[];
  },
  setRoutePath: any
) => {
  const route_check_api = IsProd ? "/v1/routes_check" : "/vb/routes_check";

  if (data.fromLocation.length === 0 || data.toLocation.length === 0) {
    return;
  }
  const route = data.fromLocation[0]["iata"] + "-" + data.toLocation[0]["iata"];
  const response_json = await request.post(route_check_api, {
    route: route,
  });
  // const response_json = await resposne.json();
  if (response_json.status === 200 && response_json.data?.program?.length > 0) {
    const path = "/route-detail?route=" + route;
    setRoutePath(path);
  }
};

export interface ApiGetDealsFlightParams {
  date_range: {
    from: string;
    to: string;
  };
  origins: {
    city: string[];
    region: string[];
    airports: string[];
  };
  arrivals: {
    city: string[];
    region: string[];
    airports: string[];
  };
}

export const getDealsFlight = (
  data: ApiGetDealsFlightParams,
  appendSearchData: any,
  setDialogOpen: any,
  setLoading: any,
  showUpsell: boolean
) => {
  recordFirstClass({ query: JSON.stringify(data), type: "deals" });
  let search_data: any = [];
  const date_groups = getGroupDatesV2(data.date_range.from, data.date_range.to, 3);
  const path = PATH_PREFIX + "routes_special_flight";

  const max_length = showUpsell
    ? MEMBERSHIP_CONFIG.routes_range[0]
    : MEMBERSHIP_CONFIG.routes_range[1];

  if (date_groups.length > max_length + 1) {
    setDialogOpen(true);
  }

  let count = Math.min(date_groups.length, max_length);

  for (let i = 0; i < Math.min(date_groups.length, max_length); i++) {
    const date_group = date_groups[i];
    const date_range = {
      from: date_group[0],
      to: date_group[date_group.length - 1],
    };
    const sub_data = {
      ...data,
      date_range,
    };
    request
      .post<any, { data: any[] }>(path + "-" + i, {
        ...sub_data,
      })
      .then(async (res) => {
        // // Map field
        (res.data || []).map(async (item: any) => {
          transform_item(item);
        });

        search_data = search_data.concat(res.data);
        search_data.sort((a: any, b: any) => {
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }
        });

        count -= 1;
        if (count <= 0) {
          appendSearchData(search_data, true);
          setLoading(false);
        } else {
          appendSearchData(search_data);
        }
      })
      .catch((e) => {
        count -= 1;
        if (count <= 1) {
          setLoading(false);
        }
      });
  }
};
